import { useCurrentUser, usePolicy } from "@ice/react";
import {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useErrorNotification } from "../hooks/useErrorNotification";
import { useWorkspaceId } from "../workspace/useWorkspaceId";
import { PermissionsContext } from "./PermissionsContext";
import { POLICY_NAMES, type PolicyName } from "./policies";

type PolicyResources = ReadonlyArray<{ readonly id: string }>;

/**
 * Shape of the resource for feature-level policies. Should the backend ever want
 * `tenancy`/`attributes` too, this is the only spot to change — but it must stay
 * STRUCTURALLY DETERMINISTIC: the SDK's atom cache indexes by structural equality,
 * so content that varies (unstable ordering, a field that appears and disappears)
 * would fire a request per render.
 */
const buildWorkspaceResources = (workspaceId: string): PolicyResources => [
    { id: workspaceId },
];

interface PolicyEvaluatorProps {
    policyName: PolicyName;
    resources: PolicyResources;
    onResult: (
        policyName: PolicyName,
        allowed: boolean,
        isError: boolean,
    ) => void;
}

/**
 * The only place in the app that calls usePolicy. As of 2.0.0-rc.2 the hook is
 * atom-based with a clean contract: it starts with isLoading=true and `results`
 * stays undefined until the evaluation is settled (and with the atom already
 * resolved from a previous mount, the first render is already settled). So every
 * render with isLoading=false ALWAYS carries a result; an error, an empty
 * `results`, or a missing resource all count as false (fail-closed). No sentinel,
 * no latch.
 *
 * The SDK always resolves on its own: it times its own request out at 5s and
 * retries once, then fails — and that failure arrives here as isError.
 *
 * The report happens in an effect rather than during render, so a MOUNT is what
 * publishes the outcome. That is deliberate and load-bearing: on an
 * already-resolved atom nothing in the deps below ever changes again, so the
 * mount effect is the only chance this component gets to speak. See the `key` on
 * this component in PermissionsProvider.
 */
const PolicyEvaluator = ({
    policyName,
    resources,
    onResult,
}: PolicyEvaluatorProps): null => {
    const { isLoading, isError, results } = usePolicy({
        action: policyName,
        resources,
    });

    useEffect(() => {
        if (isLoading) return;
        const allowed =
            !isError &&
            // `every` on an empty array is true: without this guard an evaluator
            // mounted with no resource would fail OPEN.
            resources.length > 0 &&
            resources.every(
                (resource) => results?.[resource.id]?.result === true,
            );
        onResult(policyName, allowed, isError);
    }, [isLoading, isError, results, resources, policyName, onResult]);

    return null;
};

/** Shared so the "nothing collected" reads keep a stable reference. */
const NO_PERMISSIONS: Partial<Record<PolicyName, boolean>> = {};

/**
 * How long the evaluators must stay UNMOUNTED across an identity change for the
 * previous identity's atom to be discarded.
 *
 * Remounting them is what makes them report again, but a remount in the same
 * commit is exactly what keeps the stale answer alive. @effect-atom's React
 * registry is created with `defaultIdleTTL: 400`, which also sets its
 * `timeoutResolution` to 200, so a node whose last subscriber leaves is swept
 * between 400 ms and 600 ms later — and re-subscribing inside that window does
 * not merely miss the sweep, it CANCELS it: `ensureNode` finds the node still in
 * the registry and clears its timeout.
 *
 * Waiting it out is what buys a genuine re-evaluation, because the layer
 * underneath does key on the user: @ice/core's `policyCache` is keyed on
 * `{userId, action, resources}` (capacity 10, 30 min TTL), so a fresh atom means
 * a lookup that misses and a request that actually goes out for the new user.
 *
 * 750 ms leaves headroom over the 600 ms worst case. Being late is harmless
 * here — more decay, not less — so a loaded event loop cannot break it.
 */
const ATOM_DECAY_MS = 750;

/**
 * Gates the evaluators on the identity, holding them unmounted across a change
 * for long enough that the previous identity's atom decays. Returns whether
 * they may mount.
 *
 * Only a real change waits. The first identity arms on its own commit, and so
 * does one that follows an identity nothing was ever evaluated for — the
 * pre-hydration `":"` key, which every mount passes through, being the case
 * that matters — because there is no live atom to let go of and no page load
 * should pay for one.
 *
 * Deliberately conservative in the other direction: it waits on ANY change,
 * including a workspace switch, where the resources move too and the atom key
 * is genuinely different. That costs an organization switch a fraction of a
 * second on a loader it already shows, which is not worth the extra branch.
 */
const useDecayedIdentity = (
    identityKey: string,
    hasLiveEvaluators: boolean,
): boolean => {
    const [armedKey, setArmedKey] = useState(identityKey);
    // The last identity an evaluator actually mounted for: the only kind that
    // can have left a resolved atom behind.
    const evaluatedKeyRef = useRef<string | null>(null);
    const isArmed = armedKey === identityKey;

    useEffect(() => {
        if (isArmed && hasLiveEvaluators) evaluatedKeyRef.current = identityKey;
    }, [isArmed, hasLiveEvaluators, identityKey]);

    useEffect(() => {
        if (isArmed) return;
        if (evaluatedKeyRef.current === null) {
            setArmedKey(identityKey);
            return;
        }
        const timer = setTimeout(() => setArmedKey(identityKey), ATOM_DECAY_MS);
        return () => clearTimeout(timer);
    }, [isArmed, identityKey]);

    return isArmed;
};

interface Collected {
    /** The identity these outcomes were collected for. */
    readonly key: string;
    readonly permissions: Partial<Record<PolicyName, boolean>>;
    readonly hasFailed: boolean;
}

interface PermissionsProviderProps {
    children: ReactNode;
}

export const PermissionsProvider = ({
    children,
}: PermissionsProviderProps): ReactElement => {
    const workspaceId = useWorkspaceId();
    const [user] = useCurrentUser();
    const { t } = useTranslation();
    const { showTextMessage } = useErrorNotification();

    // Identity: workspace AND user. Keyed on `user?.id`, not the `user` object
    // itself, which can change reference (a session refresh, impersonation)
    // without the logical identity changing.
    const identityKey = `${workspaceId ?? ""}:${user?.id ?? ""}`;

    // The user must be hydrated: evaluate fails with "User context is not
    // available", and that failure stays in the atom cache for the life of the
    // page — evaluating too early costs the whole session.
    const canEvaluate = workspaceId !== undefined && user !== null;

    const resources = useMemo(
        () => (workspaceId ? buildWorkspaceResources(workspaceId) : []),
        [workspaceId],
    );

    // Nothing to evaluate with an empty registry, so nothing to wait for
    // either — keep the decay gate honest about that.
    const isArmed = useDecayedIdentity(
        identityKey,
        canEvaluate && POLICY_NAMES.length > 0,
    );

    const [collected, setCollected] = useState<Collected>({
        key: identityKey,
        permissions: NO_PERMISSIONS,
        hasFailed: false,
    });

    // Reading THROUGH the identity tag is the reset: an outcome collected for a
    // previous identity is not an outcome. This is why the tag exists at all
    // rather than a `key` on a wrapper that owns the state — `children` are the
    // whole app, and `useCurrentUser` hands back null on the first render of
    // every mount, so the identity ALWAYS moves once, from ":" to ":user",
    // early in the life of the tree. A reset that remounted the subtree the
    // children sit in would rebuild the app on every page load. Nothing above
    // `children` is keyed, and nothing is written to state during render.
    const isCurrentIdentity = collected.key === identityKey;
    const permissions = isCurrentIdentity
        ? collected.permissions
        : NO_PERMISSIONS;
    const hasFailed = isCurrentIdentity && collected.hasFailed;

    const handleResult = useCallback(
        (policyName: PolicyName, allowed: boolean, isError: boolean) => {
            setCollected((previous) => {
                // A report always lands under the CURRENT identity, dropping
                // whatever was collected for a previous one.
                const base =
                    previous.key === identityKey
                        ? previous
                        : {
                              key: identityKey,
                              permissions: NO_PERMISSIONS,
                              hasFailed: false,
                          };
                const hasFailed = base.hasFailed || isError;
                if (
                    base === previous &&
                    base.permissions[policyName] === allowed &&
                    base.hasFailed === hasFailed
                ) {
                    return previous;
                }
                return {
                    key: identityKey,
                    permissions: { ...base.permissions, [policyName]: allowed },
                    hasFailed,
                };
            });
        },
        [identityKey],
    );

    // A policy nobody has reported on yet counts as LOADING, not as denied:
    // otherwise the gate would open with everything denied for one commit and
    // the children would get mounted, unmounted and remounted as soon as the
    // policies kicked in.
    //
    // This covers "workspace or user not resolved yet", and the decay window
    // after an identity change, without naming either: while no evaluator is
    // mounted nothing has been collected for this identity, so the gate stays
    // shut and fail-closed. And with POLICIES empty
    // `POLICY_NAMES.some(...)` is vacuously false, so nothing is held back —
    // including for a user who never arrives (mock context discarded, shell not
    // authenticated yet, an auth hiccup), which must not turn into a permanent
    // loader.
    const isLoading = POLICY_NAMES.some(
        (name) => permissions[name] === undefined,
    );

    // One toast per identity, no matter how many policies failed: without it,
    // the gated actions would simply be absent, indistinguishable from a user
    // who does not have the permission. The ref — not the dependency list — is
    // what makes it unique: the effect would re-fire on StrictMode's double
    // mount and on a language change. It remembers WHICH identity it spoke for,
    // since nothing remounts it any more.
    const notifiedForRef = useRef<string | null>(null);
    useEffect(() => {
        if (!hasFailed || notifiedForRef.current === identityKey) return;
        notifiedForRef.current = identityKey;
        showTextMessage(t("errors.messages.evaluatePermissions"));
    }, [hasFailed, identityKey, showTextMessage, t]);

    const value = useMemo(
        () => ({ permissions, isLoading }),
        [permissions, isLoading],
    );

    return (
        <PermissionsContext.Provider value={value}>
            {canEvaluate &&
                isArmed &&
                POLICY_NAMES.map((policyName) => (
                    <PolicyEvaluator
                        // Keyed on the identity, so an identity change REMOUNTS
                        // the evaluator. That is the only thing that makes it
                        // report again: with an unchanged {action, resources}
                        // the SDK's atom is already resolved and usePolicy hands
                        // back the same Result, so no dependency of its effect
                        // would ever move. Only `children` must stay out of
                        // this — see the identity tag above. The remount is
                        // also deliberately NOT in the same commit; see
                        // useDecayedIdentity.
                        key={`${identityKey}:${policyName}`}
                        policyName={policyName}
                        resources={resources}
                        onResult={handleResult}
                    />
                ))}
            {children}
        </PermissionsContext.Provider>
    );
};
